import { ref } from 'vue';
import axios from 'axios';
import { Channel } from '@/types/Channels';
import { sendGraphQLQuery } from '@/services/gql-util';

export function useChannels() {
    const channelTree = ref<Channel[]>([]);
    const channelList = ref<Channel[]>([]);
    const uploaderChannelTree = ref<Channel[]>([]);
    const channelSort = ref<string>('name'); // Default sort order
    const channelOrder = ref<string>('asc'); // Default order field

    /**
     * Fetches the channel tree from the server and updates the reactive state.
     * @param vhost The virtual host ID to filter channels
     */
    const getChannelTree = async (vhost: string): Promise<void> => {
	    const query = `
	    query (
		    $vhost: Int
		    $visibility: String
		    $order: String
	    ) {
		    Channels {
			    channelTree(
				    vhost: $vhost
				    visibility: $visibility
				    order: $order
			    ) {
				    id
				    name
				    description
				    shortname
				    status
				    vhost
				    visibility
				    parentchannel
				    children {
					    id
					    name
					    description
					    shortname
					    status
					    vhost
					    visibility
					    parentchannel
					    children {
						    id
						    name
						    description
						    shortname
						    status
						    vhost
						    visibility
						    parentchannel
						    children {
							    id
							    name
							    description
							    shortname
							    status
							    vhost
							    visibility
							    parentchannel
							    children {
								    id
								    name
								    description
								    shortname
								    status
								    vhost
								    visibility
								    parentchannel
								    children {
									    id
									    name
									    description
									    shortname
									    status
									    vhost
									    visibility
									    parentchannel
									    children {
										    id
										    name
										    description
										    shortname
										    status
										    vhost
										    visibility
										    parentchannel
									    }
								    }
							    }
						    }
					    }
				    }
			    }
		    }
	    }
	    `;

	    try {
		    const data = await sendGraphQLQuery(query, { 
			    vhost: +vhost,
		    });

		    if (data?.Channels?.channelTree) {
			    channelList.value = [...data.Channels.channelTree];
		    } else {
			    console.error('Unexpected response structure:', data);
			    channelList.value = [];
		    }
	    } catch (error) {
		    console.error('Error fetching channel tree:', error);
		    channelList.value = [];
		    throw error;
	    }
    };

    const getChannels = async (vhost:string): Promise<void> => {
	    const query = `
	    query (
		    $vhost: Int
		    $parentId: Int
		    $visibility: String
		    $order: String
	    ) { Channels {
		    channelList(
			    vhost: $vhost
			    parentId: $parentId
			    visibility: $visibility
			    order: $order
		    ) {
			    id
			    name
			    description
			    shortname
			    status
			    vhost
			    visibility
		    }
	    }
	    }
	    `;
	    const data = await sendGraphQLQuery(query, {vhost:+vhost  });
	    channelList.value =[ ...data.Channels.channelList];
    };

    /**
     * Adds a media item to a channel.
     * @param channelId The ID of the channel to add the media to.
     * @param mediaId The ID of the media to add to the channel.
     * @returns A promise that resolves when the API call is complete.
     */
    const addMediaToChannel = async (channelId: number, mediaId: number): Promise<void> => {
        try {
            const response = await axios.post('/services/json', {
                method: 'channels.addMedia',
                channelID: channelId,
                mediaID: mediaId,
            });

            if (response.data.status === false) {
                console.error('Error adding media to channel:', response.data.result);
                throw new Error(response.data.result);
            } else {
                console.log('Media successfully added to channel!');
            }
        } catch (error) {
            console.error('Error adding media to channel:', error);
            throw error;
        }
    };

    /**
     * Creates a new channel
     * @param name The name of the new channel
     * @param parentId The parent channel ID (default: 0 for root)
     * @param vhost The virtual host ID
     * @returns A promise that resolves with the new channel ID
     */
    const createChannel = async (name: string, parentId: number = 0, vhost: string): Promise<number> => {
        try {
            const response = await axios.post('/services/json', {
                method: 'channels.create',
                name,
                parentID: parentId,
                vhostID: vhost,
            });

            if (response.data.status === false) {
                throw new Error(response.data.result);
            }
            return response.data.result;
        } catch (error) {
            console.error('Error creating channel:', error);
            throw error;
        }
    };

    /**
     * Updates channel information
     * @param channelId The ID of the channel to update
     * @param data The updated channel data
     */
    const updateChannel = async (channelId: number, data: Partial<Channel>): Promise<void> => {
        try {
            const response = await axios.post('/services/json', {
                method: 'channels.update',
                channelID: channelId,
                ...data,
            });

            if (response.data.status === false) {
                throw new Error(response.data.result);
            }
        } catch (error) {
            console.error('Error updating channel:', error);
            throw error;
        }
    };

    return {
        channelTree,
        channelList,
        getChannels,
        uploaderChannelTree,
        channelSort,
        channelOrder,
        addMediaToChannel,
        getChannelTree,
        createChannel,
        updateChannel,
    };
}
